import { ScrollArea } from "@/components/ui/scroll-area";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, MessageSquare } from "lucide-react";
import { InboxListItem } from "./inbox-list-item";
import { useState } from "react";
import type { Inbox } from "@/types/inbox";

interface InboxListProps {
  inboxes: Inbox[];
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
  statusFilter: string | undefined;
  onStatusFilterChange: (status: string | undefined) => void;
}

export function InboxList({
  inboxes,
  loading,
  selectedId,
  onSelect,
  statusFilter,
  onStatusFilterChange,
}: InboxListProps) {
  const [search, setSearch] = useState("");

  const filtered = inboxes.filter((inbox) => {
    if (!search) return true;
    const query = search.toLowerCase();
    const contactName = (inbox.contact_name || "").toLowerCase();
    const contactPhone = (inbox.contact_phone || "").toLowerCase();
    const contactId = String(inbox.id_contact);
    return (
      contactName.includes(query) ||
      contactPhone.includes(query) ||
      contactId.includes(query)
    );
  });

  const sorted = [...filtered].sort(
    (a, b) =>
      new Date(b.last_message_at || b.datetime_add).getTime() -
      new Date(a.last_message_at || a.datetime_add).getTime(),
  );

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 border-b p-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Conversas</h2>
          <Badge variant="secondary" className="text-[10px]">
            {inboxes.length}
          </Badge>
        </div>

        <InputGroup>
          <InputGroupAddon align="inline-start">
            <Search className="h-4 w-4" />
          </InputGroupAddon>
          <InputGroupInput
            placeholder="Buscar por nome ou telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>

        <Tabs
          value={statusFilter || "all"}
          onValueChange={(v) =>
            onStatusFilterChange(v === "all" ? undefined : v)
          }
        >
          <TabsList className="w-full">
            <TabsTrigger value="all" className="flex-1 text-xs">
              Todos
            </TabsTrigger>
            <TabsTrigger value="open" className="flex-1 text-xs">
              Abertos
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex-1 text-xs">
              Pendentes
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="space-y-1 p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
            <MessageSquare className="h-8 w-8 opacity-20" />
            <p className="text-sm">Nenhuma conversa encontrada</p>
            {search && <p className="text-xs">Tente buscar por outro termo</p>}
          </div>
        ) : (
          <div className="space-y-0.5 p-1">
            {sorted.map((inbox) => (
              <InboxListItem
                key={inbox.id}
                inbox={inbox}
                isSelected={inbox.id === selectedId}
                onClick={() => onSelect(inbox.id)}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
